import { AnalyticsStore, ContextStore, ExperimentStore, PageStore } from 'bernie-plugin-mobx';
import { inject, observer } from 'mobx-react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import * as React from 'react';

import { AdvertisingStore } from 'bernie-plugin-ads-loader-script';
import { withLocalization } from 'bernie-l10n';

/* eslint-disable max-lines */
import { Experiment, ExperimentControl, ExperimentVariant } from '@shared-ui/experiment-context';
import { ILocalization } from '@shared-ui/localization-context';
import { Qualtrics } from '@shared-ui/customer-qualtrics';

import { MultiItemStore } from 'stores/multi-item-store';
import { SearchEventsContextProvider } from 'src/common/stores/contexts/clickstreamEvents/search-events-context-provider';
import { SearchResultsStore } from 'stores/search-results-store';
import { UIPageStore } from 'stores/ui-page-store';
import { View, ViewTrackerContextProvider } from 'src/common/stores/contexts/viewTracker';

import { SearchPageName } from 'constants/pages';

import { Level, liveAnnounce } from 'uitk-react-utils';
import { UitkSpacing } from 'uitk-react-spacing';

import { MasterPage, MasterPageRoute } from 'components/layout/pages/master-page';
import { ShortlistProvider } from 'components/common/shortlist/shortlist-provider';
import { TemplateRenderer } from 'components/search/template-components/template-renderer';

interface SearchProps extends RouteComponentProps {
  advertising?: AdvertisingStore;
  analytics?: AnalyticsStore;
  context?: ContextStore;
  experiment?: ExperimentStore;
  l10n?: ILocalization;
  multiItem?: MultiItemStore;
  page?: PageStore;
  route?: MasterPageRoute;
  searchResults?: SearchResultsStore;
  uiPage?: UIPageStore;
}
interface SearchState {
  lastLoadingStateMessage: string;
  view: View;
}

@withRouter
@withLocalization
@inject('advertising', 'analytics', 'context', 'experiment', 'multiItem', 'searchResults', 'uiPage')
@observer
class SearchComponent extends React.Component<SearchProps, SearchState> {
  fetchOnScrollCalled = false;

  constructor(props) {
    super(props);

    this.handleOnDismiss = this.handleOnDismiss.bind(this);
    this.fetchOnScroll = this.fetchOnScroll.bind(this);
    this.setView = this.setView.bind(this);

    this.state = {
      lastLoadingStateMessage: '',
      view: View.LISTINGS,
    };

    if (
      this.props.searchResults &&
      this.props.searchResults.searchCriteria &&
      this.props.searchResults.searchCriteria.multiProp === 'true'
    ) {
      this.props.multiItem && this.props.multiItem.getOrCreateCart(this.props.context);
    }
    this.props.advertising?.includeAd();
    if (this.props.multiItem?.multiItemContext) {
      this.props.advertising?.trigger(SearchPageName[this.props.multiItem.multiItemContext.packageType]);
    } else {
      this.props.advertising?.trigger('page.Hotels.Search');
    }
  }

  get loading() {
    const { searchResults, uiPage } = this.props;

    return searchResults.isLoading && !uiPage.displayProgressIndicator;
  }

  componentDidMount() {
    this.announceLoadingState();
    this.trackDisambiguationSearch();
    this.trackSearchResultsError();
  }

  trackDisambiguationSearch() {
    if (this.props.multiItem && this.props.multiItem.isDisambigSearch()) {
      if (this.props.analytics && typeof this.props.analytics.trackEvent === 'function') {
        const events = [{ name: 'SearchResultsDisambig', error: false }];
        this.props.analytics.trackEvent(this.props.multiItem.referrer, 'package disambig search', null, events);
      }
    }
  }

  trackSearchResultsError() {
    const { isEmpty, isError } = this.props.searchResults;

    if (isEmpty || isError) {
      if (this.props.analytics && typeof this.props.analytics.trackEvent === 'function') {
        this.props.analytics.trackEvent('SorryMessage', 'SorryMessage', null);
      }
    }
  }

  componentDidUpdate() {
    this.announceLoadingState();
  }

  announceLoadingState() {
    const { isLoading, isError, isEmpty } = this.props.searchResults;
    const { l10n } = this.props;
    const accessibilityLabel =
      this.props.searchResults?.sortAndFilter?.options?.accessibilityLabel ??
      l10n.formatText('hotels.searchResults.resultsUpdated.liveAnnounce');

    let message;
    if (isLoading) {
      message = l10n.formatText('hotels.searchResults.resultsLoading.liveAnnounce');
    } else if (isError) {
      message = l10n.formatText('hotels.searchResults.errorRetrievingResults.liveAnnounce');
    } else if (isEmpty) {
      message = l10n.formatText('hotels.searchResults.emptyResults.liveAnnounce');
    } else {
      message = accessibilityLabel;
    }
    if (message !== this.state.lastLoadingStateMessage) {
      liveAnnounce(message, Level.POLITE);
      this.setState({ lastLoadingStateMessage: message });
    }
  }

  handleOnDismiss() {
    const { history } = this.props;
    history.goBack();
  }

  setView(view: View) {
    this.setState({ view });
  }

  render() {
    const { context, experiment, searchResults, multiItem } = this.props;
    const isMixMatchCart = multiItem && multiItem.cartInfo && multiItem.cartInfo.identifier === 'mixAndMatch';

    if (isMixMatchCart && multiItem.itemCount > 0) {
      multiItem.emptyCart(context);
    }

    const controlExperience = (
      <div className="constrain-width">
        <TemplateRenderer />
      </div>
    );
    const fullPageExperience = (
      // eslint-disable-next-line camelcase
      <UitkSpacing margin={{ extra_large: { inlinestart: 'twelve', inlineend: 'twelve' } }}>
        <div>
          <TemplateRenderer />
        </div>
      </UitkSpacing>
    );
    const shouldDisplayFullPageExperience =
      experiment.exposures.lodging_reconnect_travel_hackathon?.bucket === 1 ||
      experiment.exposures.lodging_improve_page_layout?.bucket === 1;
    const templateContent = shouldDisplayFullPageExperience ? fullPageExperience : controlExperience;

    return (
      <ViewTrackerContextProvider viewTracker={{ view: this.state.view, setView: this.setView }}>
        <SearchEventsContextProvider searchEvents={searchResults.trackedTraces}>
          <ShortlistProvider>
            <MasterPage
              route={this.props.route}
              searchCriteria={searchResults.searchCriteria}
              navigationTabs={searchResults.navigationTabs}
              pageHeader={searchResults.resultsHeading}
              templateContent={templateContent}
            />
            <Experiment name="Lodging_PWA_Qualtrics">
              <ExperimentControl />
              <ExperimentVariant bucket={1}>
                <Qualtrics />
              </ExperimentVariant>
            </Experiment>
          </ShortlistProvider>
        </SearchEventsContextProvider>
      </ViewTrackerContextProvider>
    );
  }

  fetchOnScroll() {
    if (!this.fetchOnScrollCalled) {
      const { analytics, context, multiItem, searchResults, experiment, uiPage } = this.props;
      this.fetchOnScrollCalled = true;
      searchResults.showMoreHotels(analytics, context, multiItem, uiPage, experiment);
    }
  }
}

export default React.memo(SearchComponent);
